import { ArrowRight, ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { Card } from '@/components/ui/card'
import type { BridgeInsight } from '@/services/api/graph'
import type { BridgeGroup } from '../selectors'
import { BridgeRow } from './BridgeRow'

export interface BridgeGroupCardProps {
  group: BridgeGroup
  labelA: string
  labelB: string
  onBridgeClick?: (bridge: BridgeInsight) => void
  onOpenDocument?: (documentId: string) => void
}

const INITIAL_VISIBLE = 3

export function BridgeGroupCard({
  group,
  labelA,
  labelB,
  onBridgeClick,
  onOpenDocument,
}: BridgeGroupCardProps) {
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? group.bridges : group.bridges.slice(0, INITIAL_VISIBLE)
  const overflow = group.bridges.length - INITIAL_VISIBLE
  return (
    <Card size="compact" className="p-3">
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
          <span className="inline-flex items-center gap-1 rounded-md px-1.5 h-5 bg-chart-1/10 text-chart-1">
            {labelA}
          </span>
          <ArrowRight className="size-3 text-muted-foreground" />
          <span className="inline-flex items-center gap-1 rounded-md px-1.5 h-5 bg-chart-2/10 text-chart-2">
            {labelB}
          </span>
        </div>
        <span className="text-3xs tabular-nums text-muted-foreground">
          {group.bridges.length} bridge{group.bridges.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="space-y-1.5">
        {visible.map((b, i) => (
          <BridgeRow
            key={`${b.entity_a_id}-${b.entity_b_id}-${i}`}
            bridge={b}
            onClick={() => onBridgeClick?.(b)}
            onOpenDocument={
              b.document_id && onOpenDocument
                ? () => onOpenDocument(b.document_id as string)
                : undefined
            }
          />
        ))}
        {!expanded && overflow > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="w-full text-3xs text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1 py-1 max-md:py-2.5 max-md:text-xs"
          >
            <ChevronDown className="size-3" /> +{overflow} more
          </button>
        )}
      </div>
    </Card>
  )
}
